import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { gekkoApi } from '@gekkovet-oy/shared-components';

interface NamedRef {
  id: number;
  name: string;
}

interface DiseaseInfo {
  id: number;
  name?: string;
  info?: string;
  how_to_confirm?: string;
  how_to_exclude?: string;
  treatment_information?: string;
  follow_up?: string;
  disease_causes?: NamedRef[];
  sources?: NamedRef[];
}

interface DiseaseDialogProps {
  open: boolean;
  diseaseId: number | null;
  onClose: () => void;
}

export const DiseaseDialog = ({
  open,
  diseaseId,
  onClose,
}: DiseaseDialogProps) => {
  const [info, setInfo] = useState<DiseaseInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !diseaseId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setInfo(null);
    gekkoApi
      .get<DiseaseInfo>(`/diseases/${diseaseId}`)
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err?.message || 'Failed to load disease information.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, diseaseId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{info?.name || `Disease ${diseaseId ?? ''}`}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {info && !loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {info.info && <Section title="Overview" body={info.info} />}
            {info.how_to_confirm && (
              <Section title="How to confirm" body={info.how_to_confirm} />
            )}
            {info.how_to_exclude && (
              <Section title="How to exclude" body={info.how_to_exclude} />
            )}
            {info.treatment_information && (
              <Section
                title="Treatment information"
                body={info.treatment_information}
              />
            )}
            {info.follow_up && (
              <Section title="Follow-up" body={info.follow_up} />
            )}
            {info.disease_causes && info.disease_causes.length > 0 && (
              <ListSection
                title="Causes & risk factors"
                items={info.disease_causes}
              />
            )}
            {info.sources && info.sources.length > 0 && (
              <ListSection title="Sources" items={info.sources} />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SectionHeading = ({ title }: { title: string }) => (
  <Typography
    variant="overline"
    color="primary"
    sx={{
      fontWeight: 700,
      letterSpacing: 0.8,
      display: 'block',
      lineHeight: 1.4,
      mb: 0.5,
    }}
  >
    {title}
  </Typography>
);

const ListSection = ({
  title,
  items,
}: {
  title: string;
  items: NamedRef[];
}) => (
  <Box>
    <SectionHeading title={title} />
    <List dense disablePadding>
      {items.map((item) => (
        <ListItem key={item.id} disableGutters sx={{ py: 0.25 }}>
          <ListItemText
            primaryTypographyProps={{ variant: 'body2' }}
            primary={item.name}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

const Section = ({ title, body }: { title: string; body: string }) => (
  <Box>
    <SectionHeading title={title} />
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {body}
    </Typography>
  </Box>
);
